import { AxiosResponse } from 'axios';
import {
  AttachCertificateDetails,
  AttachCertificateDetailsView,
  CertificateContracts,
  CertificateContractsView,
  EligibleContract,
  GroupZeroCarbonCartificate,
  GroupZeroCarbonCartificateView,
  GroupZeroCarbonStatistics,
  GroupZeroCarbonStatisticsView,
  PreviewAddContractView,
  RequestCertificate,
  ZeroCarbonLetter,
  ZeroCarbonProject,
  ZeroCarbonProjectView,
} from '~/types/zeroCarbon';
import api from '~/utils/api';
import { Pagination, SelectItem } from '~/types';
import { formatCurrency } from '~/utils/formatCurrency';
import { formatInTimeZone } from '~/utils/formatToTimeZone';

export async function getEligibleContracts(
  id_grupo_economico: string,
): Promise<Pagination<EligibleContract>> {
  const { data } = await api.get<Pagination<EligibleContract>>(
    `grupo_economico_projetos_co2/contratos_elegiveis`,
    {
      params: {
        id_grupo_economico,
      },
    },
  );

  return data;
}

export async function getGroupZeroCarbonProjects(): Promise<
  ZeroCarbonProjectView[]
> {
  const { data } = await api.get<Pagination<ZeroCarbonProject>>(
    'grupo_economico_projetos_co2/projetos_co2/',
  );

  const formatted = data.results.map(project => ({
    ...project,
    formattedQuantidadeCo2Contratado: Number(
      project.quantidade_co2_contratado,
    ).toLocaleString('pt-BR', {
      maximumFractionDigits: 0,
    }),
    formattedQuantidadeCo2Disponivel: Number(
      project.quantidade_co2_disponivel,
    ).toLocaleString('pt-BR', {
      maximumFractionDigits: 0,
    }),
    formattedQuantidadeCo2Utilizado: Number(
      project.quantidade_co2_utilizado,
    ).toLocaleString('pt-BR', {
      maximumFractionDigits: 0,
    }),
  }));

  return formatted;
}

export async function solicitarCertificado(
  data: RequestCertificate,
): Promise<void> {
  return api.post('grupo_economico_projetos_co2/solicitar_certificado', data);
}

export async function adicionarCertificado(data: any): Promise<void> {
  return api.post('grupo_economico_projetos_co2/adicionar_certificado', data, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
}

export async function postPreviewAddContract(
  lista_contratos: any[],
): Promise<PreviewAddContractView> {
  const { data } = await api.post<PreviewAddContractView>(
    `grupo_economico_projetos_co2/preview_add_contrato`,
    {
      contratos: lista_contratos,
    },
  );

  const formattedPreview = data.preview.map(preview => ({
    ...preview,
    formattedConsumoInicialKg: formatCurrency(
      Number(preview.consumo_inicial_kg),
    ).replace('R$', ''),
    formattedConsumoMensalKg: formatCurrency(
      Number(preview.consumo_mensal_kg),
    ).replace('R$', ''),
    formattedConsumoTotalKg: formatCurrency(
      Number(preview.consumo_total_kg),
    ).replace('R$', ''),
    formattedConsumoDisponivelKg: formatCurrency(
      Number(preview.consumo_disponivel_kg),
    ).replace('R$', ''),
  }));

  const formattedData = {
    ...data,
    preview: formattedPreview,
    formattedSaldoCo2Kg: formatCurrency(Number(data.saldo_co2_kg)).replace(
      'R$',
      '',
    ),
    formattedTotalASerCompensadoT: Number(
      data.total_a_ser_compensado_t,
    ).toLocaleString('pt-BR', {
      maximumFractionDigits: 0,
    }),
    formattedTotalConsumidoKg: formatCurrency(
      Number(data.total_consumido_kg),
    ).replace('R$', ''),
  };
  return formattedData;
}

export async function getGroupCertificates(
  id_grupo_economico: string,
): Promise<Pagination<GroupZeroCarbonCartificateView>> {
  const { data } = await api.get<Pagination<GroupZeroCarbonCartificate>>(
    `grupo_economico_projetos_co2/listar`,
    {
      params: {
        id_grupo_economico,
      },
    },
  );

  const formattedData: Pagination<GroupZeroCarbonCartificateView> = {
    ...data,
    results: data.results.map(certificate => ({
      ...certificate,
      formattedDataDeCertificacao: certificate.data_de_certificacao
        ? formatInTimeZone(
            `${certificate.data_de_certificacao}T00:00:00Z`,
            'dd/MM/yyyy',
          )
        : '-',
      formattedStatus:
        certificate.status === 0
          ? 'Certificado Solicitado'
          : certificate.status === 1
          ? 'Enviado ao Cliente'
          : 'Certificado Adicionado',
      formattedConsumoTotalCo2Kg: Number(
        certificate.total_consumido_co2_kg,
      ).toLocaleString('pt-BR', {
        maximumFractionDigits: 0,
      }),
      statusColor:
        certificate.status === 0
          ? 'orange'
          : certificate.status === 1
          ? 'cyan'
          : 'green',
    })),
  };

  return formattedData;
}

export async function getGroupZeroCarbonStatistics(
  id_grupo_economico: string,
): Promise<GroupZeroCarbonStatisticsView> {
  const { data } = await api.get<GroupZeroCarbonStatistics>(
    'grupo_economico_projetos_co2/estatisticas/',
    {
      params: {
        id_grupo_economico,
      },
    },
  );

  const formattedData: GroupZeroCarbonStatisticsView = {
    ...data,
    formattedTotalConsumidoCo2Kg: formatCurrency(
      Number(data.total_consumido_co2_kg),
    ).replace('R$', ''),
    formattedTotalCompensadoCo2T: Number(
      data.total_compensado_co2_t,
    ).toLocaleString('pt-BR', {
      maximumFractionDigits: 0,
    }),
    formattedSaldoCo2Kg: formatCurrency(Number(data.saldo_co2_kg)).replace(
      'R$',
      '',
    ),
  };

  return formattedData;
}

export async function getCertificateContracts(
  grupo_economico_projeto_co2: string,
): Promise<CertificateContracts[]> {
  const { data } = await api.get<Pagination<CertificateContracts>>(
    'grupo_economico_projetos_co2/contratos_vinculados',
    {
      params: {
        grupo_economico_projeto_co2,
      },
    },
  );

  return data.results;
}

export async function getLinkedContracts(
  grupo_economico_projeto_co2: string,
): Promise<AttachCertificateDetailsView> {
  const { data } = await api.get<AttachCertificateDetails>(
    `grupo_economico_projetos_co2/${grupo_economico_projeto_co2}/get_detalhe_certificado/`,
  );

  const formattedContracts = data.contratos?.map(contract => ({
    ...contract,
    formattedConsumoInicialCo2Kg: formatCurrency(
      Number(contract.consumo_inicial_co2_kg),
    ).replace('R$', ''),
    formattedConsumoMensalCo2Kg: formatCurrency(
      Number(contract.consumo_mensal_co2_kg),
    ).replace('R$', ''),
    formattedConsumoTotalCo2Kg: formatCurrency(
      Number(contract.consumo_total_co2_kg),
    ).replace('R$', ''),
  }));

  const formattedData: AttachCertificateDetailsView = {
    ...data,
    contratos: formattedContracts,
    formattedTotalConsumidoCo2Kg: formatCurrency(
      Number(data.total_consumido_co2_kg),
    ).replace('R$', ''),
    formattedTotalCompensadoCo2t: Number(
      data.total_compensado_co2_t,
    ).toLocaleString('pt-BR', {
      maximumFractionDigits: 0,
    }),
    formattedSaldoCo2Kg: formatCurrency(Number(data.saldo_co2_kg)).replace(
      'R$',
      '',
    ),
  };

  return formattedData;
}

export async function putAttachCertificate(
  id_projeto_co2: string,
  obj: any,
): Promise<void> {
  return api.put(
    `grupo_economico_projetos_co2/${id_projeto_co2}/anexar_certificado`,
    obj,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    },
  );
}

export async function downloadCertificate(
  id: number | string,
  tipo: 'certificadora' | 'validadora',
): Promise<AxiosResponse> {
  return api.get(
    `grupo_economico_projetos_co2/${id}/download_anexo_certificado_${tipo}`,
    {
      headers: {
        'Content-Type': 'application/pdf',
      },
      responseType: 'blob' as 'json',
    },
  );
}

export async function visualizarCarta(
  id_projeto_co2: string,
): Promise<ZeroCarbonLetter> {
  const { data } = await api.get<ZeroCarbonLetter>(
    `grupo_economico_projetos_co2/${id_projeto_co2}/visualizar_carta`,
  );
  return data;
}

export async function getTodosCertificadosContrato(
  id_contrato: string,
  id_adendoaditivo?: string,
): Promise<CertificateContractsView[]> {
  const { data } = await api.get<Pagination<CertificateContracts>>(
    'grupo_economico_projetos_co2/get_todos_certificados',
    {
      params: {
        id_contrato,
        id_adendoaditivo,
      },
    },
  );

  const formattedData = data.results.map(certificate => ({
    ...certificate,
    formattedConsumoInicialCo2Kg: formatCurrency(
      Number(certificate.consumo_inicial_co2_kg),
    ).replace('R$', ''),
    formattedConsumoMensalCo2Kg: formatCurrency(
      Number(certificate.consumo_mensal_co2_kg),
    ).replace('R$', ''),
    formattedConsumoTotalCo2Kg: formatCurrency(
      Number(certificate.consumo_total_co2_kg),
    ).replace('R$', ''),
    formattedConsumoCompensadoCo2Kg: formatCurrency(
      Number(certificate.consumo_compensado_co2_kg),
    ).replace('R$', ''),
    formattedStatus:
      certificate.status === 0
        ? 'Certificado Solicitado'
        : certificate.status === 1
        ? 'Enviado ao cliente'
        : 'Certificado Adicionado',
    statusColor:
      certificate.status === 0
        ? 'orange'
        : certificate.status === 1
        ? 'cyan'
        : 'green',
    formattedDataDeCertificacao: certificate.data_de_certificacao
      ? formatInTimeZone(
          `${certificate.data_de_certificacao}T00:00:00Z`,
          'dd/MM/yyyy',
        )
      : '-',
  }));

  return formattedData;
}
